import { Suspense, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'motion/react';
import { useDocumentTitle } from './documentTitle';
import { TakeFocus, useFocusMemory, useRestoreFocusOnReturn, useTakeFocusOnOpen } from './focus';
import { LayerErrorBoundary, type LayerExit } from './LayerErrorBoundary';
import { nav } from './nav';
import type { NavigatorState, StackEntry } from './navigator';
import { coverDelayMs, layerVariants, type LayerMotion } from './pageMotion';
import { PaneActiveContext } from './pane';
import { StackRoutes } from './screens';
import { useDelayedFlag } from './useDelayedFlag';
import styles from './NavigatorView.module.css';

// Renders the root (shell or gate) and the stack above it, bottom → top.
//
// Pushed pages use the Cupertino page transition (the Flutter app pushes
// MaterialPageRoutes, which are platform-dependent; spec 01 open question 3):
// `layerVariants` (pageMotion.ts). Modal layers (Book Details, player) animate
// themselves; inside AnimatePresence they can play exit animations when
// removed.
//
// Desktop layout (signed in): the chrome (sidebar and now-playing bar) is
// drawn here, above the base and below the stack. The base and pushed pages
// fill the content area beside and above it, so the chrome stays live under
// every page; modal layers cover the whole window, chrome included.

interface LayerFlags {
  /** A pushed page sits directly above (parallax shift). */
  covered: boolean;
  /** Any pushed page is above: hide once the push transition finished. */
  obscured: boolean;
  /** Anything is above: not interactive. */
  below: boolean;
}

function flagsFor(entries: StackEntry[], index: number): LayerFlags {
  const above = entries.slice(index + 1);
  return {
    covered: above[0]?.route.kind === 'pushed',
    obscured: above.some((e) => e.route.kind === 'pushed'),
    below: above.length > 0,
  };
}

/** A failed pushed page goes back; a failed modal layer closes (LayerErrorBoundary). */
const PAGE_EXIT: LayerExit = { kind: 'back', onLeave: () => nav.back() };
const MODAL_EXIT: LayerExit = { kind: 'close', onLeave: () => nav.closeModal() };

interface StackLayerProps {
  entry: StackEntry;
  zIndex: number;
  flags: LayerFlags;
  motionState: LayerMotion;
}

function StackLayer({ entry, zIndex, flags, motionState }: StackLayerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isPresent = useIsPresent();
  const hidden = useDelayedFlag(flags.obscured, coverDelayMs(motionState.reduced, motionState.desktop));
  // Opened by the user (not rebuilt on a cold start): the layer takes focus.
  const [takeFocus] = useState(motionState.direction !== 'none');
  useTakeFocusOnOpen(ref, takeFocus);
  const memory = useFocusMemory(ref);
  useRestoreFocusOnReturn(ref, memory, !flags.below);
  // A leaving layer is inert too, so focus inside it counts as stranded and returns below.
  const inert = flags.below || !isPresent;

  const content = (
    <PaneActiveContext value={!flags.below}>
      <LayerErrorBoundary exit={entry.route.kind === 'modal' ? MODAL_EXIT : PAGE_EXIT}>
        {/* Modal layers draw over a visible page: while their chunk loads, keep that page on screen. */}
        <Suspense fallback={<div className={entry.route.kind === 'modal' ? styles.fallbackModal : styles.fallback} aria-busy="true" />}>
          <StackRoutes location={entry.location} />
          <TakeFocus layerRef={ref} enabled={takeFocus} />
        </Suspense>
      </LayerErrorBoundary>
    </PaneActiveContext>
  );

  if (entry.route.kind === 'modal') {
    return (
      <div
        ref={ref}
        className={[styles.layer, styles.modal, hidden ? styles.obscured : ''].filter(Boolean).join(' ')}
        style={{ zIndex }}
        tabIndex={-1}
        inert={inert}
      >
        {content}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={[styles.layer, styles.page, hidden ? styles.obscured : ''].filter(Boolean).join(' ')}
      style={{ zIndex }}
      tabIndex={-1}
      // The page is the screen's main landmark (modal layers are dialogs of their own).
      role="main"
      data-page={entry.route.page}
      custom={motionState}
      variants={layerVariants}
      initial="enter"
      animate={flags.covered ? 'covered' : 'active'}
      exit="exit"
      inert={inert}
    >
      {content}
    </motion.div>
  );
}

/**
 * The desktop layout's chrome, drawn beside and under the pages (inert while a
 * modal layer covers it). The sidebar comes before the page in the DOM and
 * the now-playing bar after it, so the keyboard meets them in reading order.
 */
export interface NavigatorChrome {
  sidebar: ReactNode;
  /** The now-playing bar while a book is loaded (pages end above it); null otherwise. */
  playerBar: ReactNode | null;
}

export interface NavigatorViewProps {
  state: NavigatorState;
  /** The root layer: the signed-in shell or a gate. */
  renderBase: (flags: { interactive: boolean; obscured: boolean }) => ReactNode;
  /** Desktop layout, signed in: the sidebar and now-playing bar. */
  chrome?: NavigatorChrome;
}

export function NavigatorView({ state, renderBase, chrome }: NavigatorViewProps) {
  const reduced = useReducedMotion() ?? false;
  const desktop = chrome !== undefined;
  const motionState: LayerMotion = { direction: state.direction, reduced, desktop };
  const baseFlags = flagsFor(state.entries, -1);
  const baseHidden = useDelayedFlag(baseFlags.obscured, coverDelayMs(reduced, desktop));
  const top = state.entries[state.entries.length - 1];
  const chromeInert = top?.route.kind === 'modal';
  const baseRef = useRef<HTMLDivElement>(null);
  const baseMemory = useFocusMemory(baseRef);
  useRestoreFocusOnReturn(baseRef, baseMemory, !baseFlags.below);
  const rootRef = useRef<HTMLDivElement>(null);
  useDocumentTitle(rootRef);

  return (
    <div ref={rootRef} className={styles.root} data-chrome={desktop || undefined} data-player-bar={chrome?.playerBar ? '' : undefined}>
      {chrome && (
        // Not a layer: the document title and focus restoration skip it. The sidebar comes first, so the
        // keyboard reaches it (and its skip link) before the page; z-index keeps the chrome painted above
        // the shell and below the stack.
        <div className={styles.chrome} data-navigator-chrome="" inert={chromeInert}>
          {chrome.sidebar}
        </div>
      )}
      <motion.div
        ref={baseRef}
        className={styles.base}
        tabIndex={-1}
        custom={motionState}
        variants={layerVariants}
        initial={false}
        animate={baseFlags.covered ? 'covered' : 'active'}
        inert={baseFlags.below}
        aria-hidden={baseHidden || undefined}
      >
        {renderBase({ interactive: !baseFlags.below, obscured: baseHidden })}
      </motion.div>
      {chrome?.playerBar && (
        <div className={styles.chrome} data-navigator-chrome="" inert={chromeInert}>
          {chrome.playerBar}
        </div>
      )}
      <AnimatePresence initial={false} custom={motionState}>
        {state.entries.map((entry, index) => (
          <StackLayer key={entry.key} entry={entry} zIndex={10 + index} flags={flagsFor(state.entries, index)} motionState={motionState} />
        ))}
      </AnimatePresence>
    </div>
  );
}
